import { useEffect, useState } from 'react';

/**
 * Hook that returns a debounced copy of a value. The returned value only
 * updates after `delay` milliseconds have elapsed since `value` last changed,
 * so a fast-changing value (like search input text) settles before
 * triggering dependent work like a fetch.
 *
 * @param value The value to debounce
 * @param delay Delay in milliseconds
 * @returns The debounced value
 *
 * @example
 * ```tsx
 * function Search({ query }: { query: string }) {
 *   const debouncedQuery = useDebouncedValue(query, 200);
 *
 *   const { data } = useQuery({
 *     queryKey: ['search', debouncedQuery],
 *     queryFn: () => search(debouncedQuery),
 *   });
 * }
 * ```
 */
export function useDebouncedValue<T>(value: T, delay: number): T {
   const [debounced, setDebounced] = useState(value);
   useEffect(() => {
      const id = setTimeout(() => setDebounced(value), delay);
      return () => clearTimeout(id);
   }, [value, delay]);
   return debounced;
}
